/**
 * YouTube URL を扱う共通ユーティリティ。
 *
 * 旧 Django 環境（`app/static/js/creator_profile_new.js`）の正規表現を移植している。
 * `youtu.be/<id>` / `youtube.com/watch?v=<id>` / `youtube.com/embed/<id>` /
 * `youtube.com/v/<id>` / `youtube.com/shorts/<id>` 等の主要フォーマットに対応する。
 */

const YOUTUBE_VIDEO_ID_REGEXP =
  /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

/**
 * YouTube URL から 11 文字の動画 ID を抽出する。判定不能なら `null`。
 */
export const getYouTubeVideoId = (url: string | null | undefined): string | null => {
  if (!url) return null;
  const match = url.match(YOUTUBE_VIDEO_ID_REGEXP);
  return match ? match[1] : null;
};

/**
 * YouTube 動画のサムネイル URL を返す。`mqdefault` は 320x180（16:9）で軽量。
 */
export const getYouTubeThumbnailUrl = (videoId: string): string =>
  `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;

export interface YouTubeEmbedOptions {
  autoplay?: boolean;
  mute?: boolean;
  controls?: boolean;
  /** 関連動画を同一チャンネルに限定（YouTube 仕様変更後の唯一の手段）。 */
  rel?: boolean;
  /** iOS Safari でインライン再生（ホバープレビュー用）。 */
  playsInline?: boolean;
}

/**
 * YouTube 埋め込み iframe 用の URL を組み立てる。
 *
 * `controls=0` でネイティブの操作 UI を隠し、`rel=0` で関連動画を抑制する。
 * 自動再生はユーザー操作なしで音ありを行うとブラウザに弾かれるため、
 * 呼び出し側でホバープレビュー時は `mute: true` を付けて使う。
 */
export const getYouTubeEmbedUrl = (
  videoId: string,
  options: YouTubeEmbedOptions = {},
): string => {
  const {
    autoplay = false,
    mute = false,
    controls = true,
    rel = false,
    playsInline = false,
  } = options;

  const params = new URLSearchParams();
  params.set("autoplay", autoplay ? "1" : "0");
  params.set("mute", mute ? "1" : "0");
  params.set("controls", controls ? "1" : "0");
  params.set("rel", rel ? "1" : "0");
  if (playsInline) params.set("playsinline", "1");

  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};
